import { GraphQLResult, graphqlOperation } from "@aws-amplify/api-graphql";
import { generateClient } from "aws-amplify/api";
import { getSumoraAuthToken } from "common/functions/credentials";
import { base64ToBlob } from "common/functions/utilities";
import { MainContextContainer } from "components/Main";
import {
  CreatePublicEstatePdfFMutation,
  CreatePublicEstatePdfFMutationVariables,
  PublicEstate
} from "graphql/API";
import { createPublicEstatePdfF } from "graphql/mutations";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useParams } from "react-router";
import { getPublicEstateIdComponent } from "./functions";
import { DetailProps } from "./interfaces";

export const useDetailHooks = (props: DetailProps) => {
  const initRef = useRef(true);

  const params = useParams();

  const mainContext = useContext(MainContextContainer);

  const [companyIndexes, setCompanyIndexes] = useState<string[]>([]);
  const [estateIds, setEstateIds] = useState<string[]>([]);
  const [fontsReady, setFontsReady] = useState(!props.forAnonymous);
  const [companyLoadCompleted, setCompanyLoadCompleted] = useState<boolean[]>(
    []
  );
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isPdfDownloaded, setIsPdfDownloaded] = useState(false);

  const waiting = useMemo(() => params.id === "waiting", [params.id]);

  const actualForAnonymous = useMemo(
    () => props.forAnonymous && !mainContext.isReadyToUse,
    [mainContext.isReadyToUse, props.forAnonymous]
  );

  const publicEstateIdComponents = useMemo(() => {
    if (params.id === "waiting") {
      return [];
    } else if (params.id) {
      const ids = params.id.split(",");
      return ids.map(getPublicEstateIdComponent);
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualForAnonymous, params.id]);

  const publicEstateIds = useMemo(() => {
    return publicEstateIdComponents.map(
      (component) => component.actualPublicEstateId
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualForAnonymous, publicEstateIdComponents]);

  const completed = useMemo(
    () =>
      !waiting &&
      publicEstateIds.length > 0 &&
      publicEstateIds.length === estateIds.length &&
      companyLoadCompleted.length === estateIds.length &&
      companyLoadCompleted.every((completed) => completed) &&
      (!props.forAnonymous || fontsReady),
    [
      waiting,
      publicEstateIds.length,
      estateIds.length,
      companyLoadCompleted,
      props.forAnonymous,
      fontsReady
    ]
  );

  const onPublicEstateLoaded = useCallback((publicEstate: PublicEstate) => {
    setEstateIds(
      (estateIds) =>
        Array.from(new Set([...estateIds, publicEstate.estate?.id])) as string[]
    );
  }, []);

  const onCompanyIndexChanged = useCallback(
    (publicEstateIndex: number, companyIndex: number) => {
      setCompanyIndexes((prev) => {
        const next = [...prev];
        next[publicEstateIndex] = companyIndex.toString();
        return next;
      });
    },
    []
  );

  const onDownloadPdfClick = useCallback(
    async (url: string) => {
      setIsDownloadingPdf(true);

      try {
        const client = generateClient();
        const res = (await client.graphql({
          ...graphqlOperation(createPublicEstatePdfF, {
            input: {
              url
            }
          } as CreatePublicEstatePdfFMutationVariables),
          authMode: "lambda",
          authToken: await getSumoraAuthToken()
        })) as GraphQLResult<CreatePublicEstatePdfFMutation>;

        if (res.data?.createPublicEstatePdfF?.pdf) {
          try {
            const fileName = `${
              estateIds.length === 0
                ? "物件"
                : estateIds.length === 1
                  ? estateIds[0]
                  : `${estateIds[0]}他`
            }.pdf`;

            const blob = base64ToBlob(
              res.data.createPublicEstatePdfF.pdf,
              "application/pdf"
            );

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            a.click();

            setIsPdfDownloaded(true);
          } catch (e) {
            alert("PDFのダウンロードに失敗しました。");
            window.console.error(e);
            return false;
          }
        }
      } catch (e) {
        alert("PDFのダウンロードに失敗しました。");
        window.console.error(e);
        return false;
      } finally {
        setIsDownloadingPdf(false);
      }
    },
    [estateIds]
  );

  const onCompanyLoadCompleted = useCallback(
    (index: number) => {
      setCompanyLoadCompleted((prev) => {
        const next = [...prev];

        while (index >= next.length) {
          next.push(false);
        }

        next[index] = true;

        return next;
      });
    },
    [setCompanyLoadCompleted]
  );

  useEffect(() => {
    if (initRef.current) {
      initRef.current = false;
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCompanyIndexes(
      publicEstateIdComponents.map((component) => component.companyIndex)
    );
  }, [actualForAnonymous, publicEstateIdComponents]);

  // フォント待ち effect（/pub のみ）
  useEffect(() => {
    if (!props.forAnonymous) {
      return;
    }

    let cancelled = false;

    const waitForPubPdfFonts = async () => {
      try {
        await document.fonts.ready;

        if (!cancelled) setFontsReady(true);
      } catch (e) {
        window.console.error("Pub PDF fonts failed to load", e);
      }
    };

    waitForPubPdfFonts();

    return () => {
      cancelled = true;
    };
  }, [props.forAnonymous]);

  return {
    mainContext,
    publicEstateIds,
    companyIndexes,
    waiting,
    isDownloadingPdf,
    isPdfDownloaded,
    completed,

    onPublicEstateLoaded,
    onCompanyIndexChanged,
    onDownloadPdfClick,
    onCompanyLoadCompleted
  } as const;
};
